<template>
  <div style="border-left: 1px solid #c1c1c1" class="pt-0 px-2 pb-2">
    <div class="text-right" v-if="!isMobile">
      <v-btn x-small icon @click="close()">
        <v-icon x-small>
          mdi-close
        </v-icon>
      </v-btn>
    </div>
    <v-card class="text-right caption" flat tile height="15">
      基準時刻 : {{ currentDateTime }}
    </v-card>
    <v-card class="d-flex align-center" flat tile height="30">
      <v-chip small>水位モニタリング</v-chip>
    </v-card>
    <div
      class="river-level-contents"
      :style="{
        height: isMobile ? 'calc(45dvh - 80px)' : 'calc(100dvh - 420px)'
      }"
    >
      <v-data-table
        dense
        :headers="waterHeaders"
        :items="waterLevels"
        hide-default-footer
        class="river-level-table"
        fixed-header
        :items-per-page="-1"
        @click:row="moveObservatoryDetail"
        mobile-breakpoint="0"
      >
        <template v-slot:[`item.level`]="{ item }">
          <v-chip
            :style="getStageChipStyle(item, item.level)"
            small
            label
            style="font-size: 11px"
          >
            {{ item.level }}
          </v-chip>
        </template>
        <template v-slot:[`item.graph`]="{ item }">
          <div class="graph-area">
            <v-progress-linear
              v-model="item.graph.levelProportion"
              color="#3388ff"
              height="18"
            ></v-progress-linear>
            <div
              v-if="item.graph.dangerous !== null"
              class="level-line dangerous-color"
              :style="{ left: item.graph.dangerous + '%' }"
            ></div>
          </div>
        </template>
      </v-data-table>
      <div class="d-flex caption flex-row-reverse">
        (単位: m)
      </div>
    </div>
    <v-card class="d-flex align-center mt-2" tile flat height="30">
      <v-chip small>雨量モニタリング</v-chip>
    </v-card>
    <div
      class="rainfall-contents"
      :style="{ height: isMobile ? 'calc(45dvh - 80px)' : '300px' }"
    >
      <v-data-table
        dense
        :headers="rainfallHeaders"
        :items="rainLevels"
        hide-default-footer
        class="rainfall-table"
        :items-per-page="-1"
        @click:row="moveObservatoryDetail"
        mobile-breakpoint="0"
      >
      </v-data-table>
      <div class="d-flex caption flex-row-reverse">
        (現在雨量: mm/10分, 累計雨量: mm)
      </div>
    </div>
  </div>
</template>
<script>
import L from "leaflet";
import _ from "lodash";
import { OBSERVATORY } from "../enums/Type";
import { getStageChipStyle } from "../utils/common";

export default {
  name: "Monitoring",
  components: {},
  data() {
    return {
      rainfallHeaders: [
        {
          text: "観測所",
          align: "left",
          sortable: false,
          value: "name",
          class: "data-table-header"
        },
        {
          text: "現在雨量",
          align: "right",
          sortable: false,
          value: "min10",
          width: "80px",
          class: "data-table-header"
        },
        {
          text: "累加雨量",
          align: "right",
          sortable: false,
          value: "total",
          width: "80px",
          class: "data-table-header"
        }
      ]
    };
  },
  computed: {
    waterHeaders() {
      const targets = [
        {
          text: "観測所",
          align: "left",
          sortable: false,
          value: "name",
          width: "60px",
          class: "data-table-header",
          cellClass: "obsname-info",
          mobile: true,
          desktop: true
        },
        {
          text: "現在\n水位",
          align: "right",
          sortable: false,
          value: "level",
          width: "40px",
          class: "data-table-header",
          cellClass: "master-level-info",
          mobile: true,
          desktop: false
        },
        {
          text: "水防\n待機",
          align: "right",
          sortable: false,
          value: "standby",
          width: "40px",
          class: "data-table-header standby-color",
          cellClass: "master-level-info"
        },
        {
          text: "氾濫\n注意",
          align: "right",
          sortable: false,
          value: "warning",
          width: "40px",
          class: "data-table-header warning-color",
          cellClass: "master-level-info"
        },
        {
          text: "避難\n判断",
          align: "right",
          sortable: false,
          value: "evacuation",
          width: "40px",
          class: "data-table-header evacuation-color",
          cellClass: "master-level-info"
        },
        {
          text: "氾濫\n危険",
          align: "right",
          sortable: false,
          value: "dangerous",
          width: "40px",
          class: "data-table-header dangerous-color",
          cellClass: "master-level-info"
        },
        {
          text: "氾濫\n発生",
          align: "right",
          sortable: false,
          value: "outbreak",
          width: "40px",
          class: "data-table-header outbreak-color",
          cellClass: "master-level-info",
          mobile: false,
          desktop: false
        },
        {
          text: "現在\n水位",
          align: "right",
          sortable: false,
          value: "level",
          width: "40px",
          class: "data-table-header",
          cellClass: "master-level-info",
          mobile: false,
          desktop: true
        },
        {
          text: "グラフ",
          sortable: false,
          value: "graph",
          width: "50px",
          class: "data-table-header",
          mobile: false,
          desktop: true
        }
      ];
      return targets.filter(
        target =>
          (this.isMobile && target.mobile !== false) ||
          (!this.isMobile && target.desktop !== false)
      );
    },
    waterLevels() {
      const targets = [
        "1793_29",
        "21064_3",
        "21064_1",
        "21064_2",
        "7000_22",
        "7000_23",
        "7000_25",
        "7000_28",
        "7000_30",
        "7000_31",
        "7000_32",
        "7000_33",
        "7000_34",
        "7000_35",
        "7000_36",
        "7000_37",
        "7000_38",
        "7000_381"
      ];
      const arr = this.$store.getters.observatories;
      let observatories = arr.filter(
        obs =>
          [OBSERVATORY.STAGE.code, OBSERVATORY.STAGE_KIKI.code].includes(
            obs.type
          ) && targets.includes(obs.id)
      );
      observatories = observatories.map(obs => {
        const r = Object.assign({}, obs);
        r.standby = "・";
        r.warning = "・";
        r.evacuation = "・";
        r.dangerous = "・";
        r.outbreak = "・";

        if (r.standbyLevel !== undefined) {
          r.standby = r.standbyLevel.toFixed(2);
        }
        if (r.warningLevel !== undefined) {
          r.warning = r.warningLevel.toFixed(2);
        }
        if (r.evacuationLevel !== undefined) {
          r.evacuation = r.evacuationLevel.toFixed(2);
        }
        if (r.dangerousLevel !== undefined) {
          r.dangerous = r.dangerousLevel.toFixed(2);
        }
        if (r.outbreakLevel !== undefined) {
          r.outbreak = r.outbreakLevel.toFixed(2);
        }
        if (r.type === OBSERVATORY.STAGE_KIKI.code) {
          if (r.warnLevel !== undefined) {
            r.dangerous = r.warnLevel.toFixed(2);
          }
          if (r.floodLevel !== undefined) {
            r.outbreak = r.floodLevel.toFixed(2);
          }
        }

        if (r.level === undefined || r.level === -999) {
          if (
            r.type === OBSERVATORY.STAGE_KIKI.code &&
            r.chartWaterBoxLevel !== undefined
          ) {
            r.level = "---";
          } else {
            r.level = "欠測";
          }
          r.isMissing = true;
        } else {
          r.level = r.level.toFixed(2);
          r.isMissing = false;
        }

        r.sortLevel = r.outbreak !== "・" || r.dangerous !== "・" ? 1 : 9;
        if (r.isMissing === true) {
          r.sortLevel = r.sortLevel * 10;
        }
        return r;
      });
      observatories.forEach(r => {
        let dangerousValue = null;
        if (r.dangerous !== "・") {
          dangerousValue = parseFloat(r.dangerous);
        } else if (r.outbreak !== "・") {
          dangerousValue = parseFloat(r.outbreak);
        }
        let graphMaxValue = null;
        if (dangerousValue !== null) {
          if (dangerousValue < 0) {
            graphMaxValue = parseFloat(dangerousValue) / 1.2;
          } else {
            graphMaxValue = parseFloat(dangerousValue) * 1.2;
          }
        }
        const level = r.isMissing === true ? null : parseFloat(r.level);
        if (graphMaxValue === 0) {
          graphMaxValue = 0.01;
        }

        let levelProportion = 0;
        if (r.isMissing === false && graphMaxValue !== null) {
          if (level > graphMaxValue) {
            levelProportion = 100;
          } else {
            if (graphMaxValue <= 0) {
              const offset = level + 1;
              levelProportion =
                ((graphMaxValue + offset) / (level + offset)) * 100;
            } else {
              levelProportion = (level / graphMaxValue) * 100;
            }
          }
        }
        let graphDangerous = null;
        if (dangerousValue !== null && graphMaxValue !== null) {
          if (graphMaxValue <= 0) {
            const offset = dangerousValue + 1;
            graphDangerous =
              ((graphMaxValue + offset) / (dangerousValue + offset)) * 100;
          } else {
            graphDangerous = (dangerousValue / graphMaxValue) * 100;
          }
          if (graphDangerous > 80 && graphDangerous < 90) {
            graphDangerous = 83;
          }
        }
        r.graph = {
          dangerous: graphDangerous,
          levelProportion: levelProportion
        };
        r.riskValue =
          dangerousValue !== null && r.isMissing === false
            ? levelProportion
            : 0;
        if (r.riskValue === 0 && r.isMissing === false) {
          r.riskValue = level * 0.000001;
        }
      });
      return _.orderBy(
        observatories,
        ["sortLevel", "riskValue"],
        ["asc", "desc"]
      );
    },
    rainLevels() {
      const targets = [
        "1793_16",
        "1793_18",
        "1793_40",
        "1793_45",
        "1793_46",
        "21064_1",
        "21064_2",
        "21064_3",
        "21064_4",
        "30000_36391",
        "99_07211_1_1",
        "99_07211_1_2",
        "99_07211_1_3",
        "99_07211_1_4",
        "99_07211_1_5",
        "99_07211_1_6",
        "99_07211_1_7",
        "99_07211_1_8"
      ];
      const arr = this.$store.getters.observatories;
      let observatories = arr.filter(
        obs => obs.type === OBSERVATORY.RAIN.code && targets.includes(obs.id)
      );
      observatories = observatories.map(obs => {
        const r = Object.assign({}, obs);
        r.sortMin10 = r.min10 === undefined ? -999 : r.min10;
        r.sortTotal = r.total === undefined ? -999 : r.total;
        if (r.min10 === -999) {
          r.min10 = "欠測";
        }
        if (r.total === -999) {
          r.total = "欠測";
        }
        return r;
      });
      return _.orderBy(
        observatories,
        ["sortMin10", "sortTotal"],
        ["desc", "desc"]
      );
    },
    leafletMap() {
      return this.$store.getters.leafletMap;
    },
    currentDateTime() {
      const baseDate = this.$store.getters.baseDate.clone();
      baseDate.subtract(baseDate.minute() % 10, "minutes");
      return baseDate.local().format("YYYY/MM/DD HH:mm");
    },
    isMobile() {
      return this.$route.path === "/mobile";
    }
  },
  methods: {
    close() {
      this.$store.commit("SET_IS_MONITORING", false);
    },
    async moveObservatoryDetail(info) {
      this.leafletMap.panTo(new L.LatLng(info.lat, info.lng));
      const observatory = Object.assign(info, { isMonitoring: true });
      await this.$store.dispatch(
        "FETCH_SELECTED_OBSERVATORY_DATA",
        observatory
      );
      if (this.isMobile) {
        this.$emit("selected");
      }
    },
    getStageChipStyle
  }
};
</script>

<style lang="scss">
th,
td {
  height: 24px !important;
}

.river-level-contents {
  overflow-y: scroll;
  .data-table-header {
    padding: 0 5px 0 3px !important;
    min-width: 60px;
    &.standby-color {
      color: #008000 !important;
    }
    &.warning-color {
      color: #ffa500 !important;
    }
    &.evacuation-color {
      color: #ff2800 !important;
    }
    &.dangerous-color {
      color: #ad02ad !important;
    }
    &.outbreak-color {
      color: #000000 !important;
    }
  }
  .master-level-info {
    font-size: 11px !important;
  }
  .obsname-info {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 0;
    max-width: 100px;
  }
  .theme--light.v-data-table thead tr:last-child th {
    border-bottom: 1px double black !important;
    font-weight: normal;
    text-align: center !important;
    white-space: pre-wrap;
  }
  .graph-area {
    pointer-events: none;
    position: relative;
    .level-line {
      position: absolute;
      top: 0;
      left: 50%;
      width: 2px;
      height: 100%;
      &.dangerous-color {
        background-color: #ad02ad !important;
        font-size: 10px;
      }
    }
  }
}
.river-level-table {
  td {
    font-size: 12px !important;
    padding: 0 5px 0 3px !important;
  }
}

.rainfall-contents {
  overflow-y: scroll;
  .data-table-header {
    padding: 0 5px 0 3px !important;
    min-width: 60px;
  }
  .theme--light.v-data-table thead tr:last-child th {
    border-bottom: 1px double black !important;
  }
  .data-table-header:nth-child(2) {
    background-color: #3a1e87 !important;
    color: white !important;
  }
  .data-table-header:nth-child(3) {
    background-color: #3a1e87 !important;
    color: white !important;
  }
  .theme--light.v-data-table thead tr th {
    color: black;
  }
}

.rainfall-table {
  td {
    padding: 0 5px 0 3px !important;
    font-size: 12px !important;
    line-height: 1;
    height: inherit;
  }
  tr td:nth-child(2) {
    border-right: 1px solid #3a1e87;
    border-left: 1px solid #3a1e87;
  }
  tr td:nth-child(3) {
    border-right: 1px solid #3a1e87;
  }
}
</style>

<style lang="scss">
.v-application--is-ltr .v-list-item__action:first-child,
.v-application--is-ltr .v-list-item__icon:first-child {
  margin-right: 20px;
}
</style>
